import log from '../log';
import { User } from '../db/t_user';
import time from '../time';
import { Msg } from '../db/t_msg';
import { KKAuth } from './index';
import { Auth } from '../db/t_auth';
import { TopicWithSummary } from './topic';
import { LoginDevice, TenantSettings } from '../db/t_user_devices';

export type FullProcessData = {
	name: 'begin' | 'done' | 'process' | 'sync_start' | 'error';
	process: number;
	count: number;
	/** 全量分页阶段：friend / group / label / topic（仅 process / error 等会带） */
	phase?: string;
	/** 分页接口失败时的业务/网络码（如 -1 表示连接异常） */
	code?: number;
	/** 服务端或底层返回的提示文案（可选） */
	hint?: string;
	/** 与 hint 同源，供会话页等 UI 展示「批量同步失败」条（避免各处重复拼文案） */
	error?: string;
};
export type MsgNewData = {
	tid: string; // 会话
	uid: number; // 发送人
};

export type RTCMsgNewData = {
	tid: string; // 会话
	uid: number; // 发送人
	msgs: Msg[];
};

export type MQEventData = {
	full_process?: FullProcessData; // full_process 任务积压
	userinfo?: User; // userinfo 用户信息更新或者刷新
	msg_update?: { tid: string; mid: string }; // 某条消息自己变化了
	msg_del?: { tid: string }; // 消息删除
	msg_new?: MsgNewData; // 有消息更新的会话
	rtc_msg?: RTCMsgNewData; // 有rtc消息
	open_notify?: { msg: Msg }; // 有消息更新的会话
	group_update?: { tid: string }; // 群信息有变更
	group_delete?: { tid: string }; // 群已解散，本地已清理
	topic_new?: { tidList: string[] }; // 新增了会话
	topic_qry?: { topicDetail: TopicWithSummary }; // 查找了会话
	topic_update?: { tid: string }; // 会话更新了
	topic_update_draft?: { tid: string; draft: string }; // 会话更新了
	topic_visit?: { tid: string; maxshowmid: string; utime: number }; // 会话被访问了
	topic_to_top?: { tid: string; order: number }; // 会话置顶
	topic_remark?: { tid: string; remark: string }; // 会话备注
	topic_mute?: { tid: string; mute: boolean }; // 静音
	topic_delete?: { tid: string; hidemid: string }; // 会话删除、或者叫隐藏
	friend_apply?: { count: number }; // 新的好友请求数
	friend_rel?: { friendUid: number; op: string }; // 增删好友
	user_head?: { uid: number; avatar: string }; // 用户头像变更
	user_remark?: { uid: number; remark: string }; // 用户备注
	user_black?: { uid: number; black: string; time: number }; // 用户备注
	user_devices?: { uid: number; devices: LoginDevice[] }; // 用户设备列表
	tenant_settings?: { uid: number; settings: TenantSettings }; // 租户设置更新	
	auth_fail?: { vcode?: string, msg?: string }; // 鉴权失败
	global_login?: KKAuth & { isAddAccount?: boolean; lastUid?: number }; // 全局事件 用户登陆了,isAddAccount是否是添加多账号，lastUid 添加多账号对应的上一个用户id
	group_apply?: { tid: string }; // 全局事件 用户登陆了
	msg_read?: { tid: string; mid: string; readCnt: number; targetCnt: number; msg: Msg };
	global_logout?: { vcode?: string; auth?: KKAuth & Auth; screen?: string };
	topic_refresh?: {};
	/** 全量同步等：一批会话已写入本地 DB，携带 tid 列表供 UI 增量刷新 */
	topic_batch_written?: { tids: string[] };
	user_permission?: { msgAssistant?: number; msgTranslate?: number };
	group_black?: { tid: string };
};

export type MQEventType =
	| 'contact_refresh' // 通讯录信息刷新了
	| 'topic_refresh' // 会话全量刷新
	| 'topic_batch_written' // 一批会话已落库，渐进更新列表
	| 'topic_update' // 会话更新了
	| 'topic_update_draft' //更新回话草稿
	| 'msg_update' // 某条消息内容更新了
	| 'msg_del' // 消息删除
	| 'msg_new' // 新来的会话，或者说之前本地没有
	| 'open_notify' // 消息通知
	| 'topic_new' // 新增加会话
	| 'topic_qry'
	| 'topic_visit' // 会话被访问
	| 'topic_to_top' // 会话置顶
	| 'topic_remark' // 会话备注
	| 'topic_mute' // 静音
	| 'topic_delete' // 会话删除、或者叫隐藏
	| 'group_update' // 群信息有变更
	| 'group_delete' // 群已解散
	| 'full_process' // 任务积压
	| 'userinfo' // 用户信息更新或者刷新
	| 'friend_apply' // 好友申请
	| 'friend_rel' // 增删好友
	| 'user_head' // 用户头像变更
	| 'user_remark' // 用户备注
	| 'user_black' // 用户黑名单
	| 'user_devices' // 用户登录设备变更
	| 'tenant_settings' // 租户设置变更
	| 'auth_fail' // 鉴权失败
	| 'global_logout' // 全局的事件主动登出
	| 'global_login' // 全局的事件登陆
	| 'group_apply' // 全局的事件登陆
	| 'msg_read'
	| 'user_permission'
	| 'rtc_msg'
	| 'group_black'
	| 'sync_loop_done' // 每轮增量 sync 完成
	| '*';

export type MQEventCallBack = (res: { type: MQEventType; data: MQEventData }) => void;

export class MQ {
	private listener: { [key: string]: MQEventCallBack[] } = {};
	private ok: boolean = true;
	// 可配置的派发节流间隔（毫秒）
	private tickMs: number;
	// 需要“即时派发”的高优先级事件白名单
	private immediateSet: Set<MQEventType> = new Set([
		'msg_new', // 新消息：未读角标依赖 topicCallback→GetTopicDetail，不能走 3s stash
		'msg_del', // 删消息：未读与会话摘要需尽快一致
		'msg_update', // 已读回执/消息内容更新
		'open_notify', // 系统推送转前台提示
		'topic_update', // 会话摘要/未读数变化
		'topic_new', // 新会话创建
	]);

	constructor(opts?: { tickMs?: number; immediateEvents?: MQEventType[] }) {
		this.tickMs = opts?.tickMs ?? 3000; // 默认 3s
		if (opts?.immediateEvents?.length) {
			// 允许从外部扩充/覆盖白名单
			this.immediateSet = new Set(opts.immediateEvents);
		}
		this.runable().then(() => {});
	}

	public on(evt: MQEventType, callback: MQEventCallBack) {
		if (!this.ok) {
			return;
		}
		if (this.listener[evt]) {
			let idx = this.listener[evt].findIndex((item) => item === callback);
			idx < 0 && this.listener[evt].push(callback);
		} else {
			this.listener[evt] = [];
			this.listener[evt].push(callback);
		}
	}

	public off(evt: MQEventType, callback: MQEventCallBack) {
		if (!this.ok) {
			return;
		}
		if (evt === '*') {
			const keys = Object.keys(this.listener);
			const n = keys.length;
			for (let i = 0; i < n; i++) {
				const key = keys[i];
				if (this.listener[key]) {
					let idx = this.listener[key].findIndex((item) => item === callback);
					idx > -1 && this.listener[key].splice(idx, 1);
				}
			}
		} else if (this.listener[evt]) {
			let idx = this.listener[evt].findIndex((item) => item === callback);
			idx > -1 && this.listener[evt].splice(idx, 1);
		}
	}

	// 暂存方便归并事件，慢慢调用
	private allstash: { [key: string]: { [y: string]: MQEventData } } = {};

	public emit_stash(evt: MQEventType, data: MQEventData) {
		if (!this.ok) {
			return;
		}
		// 关键事件即使传入 stash 也“直发”，避免 3 秒延迟
		if (this.immediateSet.has(evt)) {
			this.emit(evt, data, /*stash*/ false);
			return;
		}

		let stash = this.allstash[evt as string];
		if (!stash) {
			stash = {};
			this.allstash[evt as string] = stash;
		}
		if (evt === 'userinfo' && data.userinfo) {
			const k = data.userinfo.uid.toString(10);
			if (this.allstash.user_head) {
				delete this.allstash.user_head[k];
			}
			if (this.allstash.user_remark) {
				delete this.allstash.user_remark[k];
			}
			stash[k] = data;
			return;
		}
		if (evt === 'user_head' && data.user_head) {
			const k = data.user_head.uid.toString(10);
			if (!(this.allstash.userinfo && this.allstash.userinfo[k])) {
				stash[k] = data;
			}
			return;
		}
		if (evt === 'user_remark' && data.user_remark) {
			// const k = data.user_remark.uid.toString(10);
			// if (!(this.allstash.userinfo && this.allstash.userinfo[k])) {
			//     stash[k] = data;
			// }
			stash[evt] = data;
			return;
		}
		if (evt === 'user_black' && data.user_black) {
			const k = data.user_black.uid.toString(10);
			if (!(this.allstash.userinfo && this.allstash.userinfo[k])) {
				stash[k] = data;
			}
			return;
		}

		if (evt === 'user_devices' && data.user_devices) {
			const k = data.user_devices.uid.toString(10);
			stash[k] = data;
			return;
		}

		if (evt === 'tenant_settings' && data.tenant_settings) {
			const k = data.tenant_settings.uid.toString(10);
			stash[k] = data;
			return;
		}

		if (evt === 'topic_visit' && data.topic_visit) {
			const k = data.topic_visit.tid;
			if (!(this.allstash.topic_update && this.allstash.topic_update[k])) {
				stash[k] = data;
			}

			return;
		}

		if (evt === 'topic_to_top' && data.topic_to_top) {
			const k = data.topic_to_top.tid;
			if (!(this.allstash.topic_update && this.allstash.topic_update[k])) {
				stash[k] = data;
			}
			return;
		}

		if (evt === 'topic_remark' && data.topic_remark) {
			const k = data.topic_remark.tid;
			if (!(this.allstash.topic_update && this.allstash.topic_update[k])) {
				stash[k] = data;
			}
			return;
		}

		if (evt === 'topic_mute' && data.topic_mute) {
			const k = data.topic_mute.tid;
			if (!(this.allstash.topic_update && this.allstash.topic_update[k])) {
				stash[k] = data;
			}

			return;
		}

		if (evt === 'topic_update' && data.topic_update) {
			const k = data.topic_update.tid;
			if (this.allstash.topic_visit && this.allstash.topic_visit[k]) {
				delete this.allstash.topic_visit[k];
			}
			if (this.allstash.topic_to_top && this.allstash.topic_to_top[k]) {
				delete this.allstash.topic_to_top[k];
			}
			if (this.allstash.topic_remark && this.allstash.topic_remark[k]) {
				delete this.allstash.topic_remark[k];
			}
			if (this.allstash.topic_mute && this.allstash.topic_mute[k]) {
				delete this.allstash.topic_mute[k];
			}
			stash[k] = data;
			return;
		}

		if (evt === 'group_update' && data.group_update) {
			const k = data.group_update.tid;
			stash[k] = data;
			return;
		}

		if (evt === 'msg_new' && data.msg_new) {
			const k = data.msg_new.tid;
			stash[k] = data;
			return;
		}
		if (evt === 'rtc_msg' && data.rtc_msg) {
			const k = data.rtc_msg.tid;
			stash[k] = data;
			return;
		}

		if (evt === 'msg_update' && data.msg_update) {
			const k = data.msg_update.mid;
			stash[k] = data;
			return;
		}
		if (evt === 'msg_del' && data.msg_del) {
			const k = data.msg_del.tid;
			stash[k] = data;
			return;
		}

		if (evt === 'contact_refresh' || evt === 'friend_apply') {
			stash[evt] = data;
			return;
		}
		if (evt === 'full_process' && data.full_process) {
			stash[evt] = data;
			return;
		}
		if (evt === 'open_notify' && data.open_notify) {
			stash[evt] = data;
			return;
		}
		if (evt === 'global_logout') {
			stash[evt] = data;
			return;
		}
		if (evt === 'topic_new') {
			stash[evt] = data;
			return;
		}
		if (evt === 'topic_qry') {
			stash[evt] = data;
			return;
		}
		if (evt === 'user_permission') {
			stash[evt] = data;
			return;
		}
		if (evt === 'group_black') {
			stash[evt] = data;
			return;
		}

		log.warn('未知的暂存事件', evt, data);
	}

	public flush() {
		try {
			this.do_stash();
		} catch (e) {
			log.warn('flush do_stash err', e);
		}
	}

	// 取出所有暂存的任务
	private pop_all_stash(): { [key: string]: { [y: string]: MQEventData } } {
		if (!this.ok) {
			return {};
		}
		const rst = this.allstash;
		this.allstash = {};
		return rst;
	}

	private async runable() {
		while (this.ok) {
			try {
				this.do_stash();
			} catch (err) {
				log.error('处理暂存事件异常', err);
			}
			await time.sleep(this.tickMs);
		}
	}

	private do_stash() {
		const events = this.pop_all_stash();
		Object.keys(events).forEach((evt) => {
			Object.keys(events[evt]).forEach((dataKey) => {
				this.ok && this.emit(evt as MQEventType, events[evt][dataKey]);
			});
		});
	}

	public emit(evt: MQEventType, data: MQEventData, stash: boolean = false) {
		if (!this.ok) {
			log.warn('mq.emit ignore!', evt, JSON.stringify(data));
			return;
		}

		if (stash) {
			this.emit_stash(evt, data);
			return;
		}
		if (this.listener[evt]) {
			const n = this.listener[evt].length;
			for (let i = 0; i < n; i++) {
				try {
					this.listener[evt][i]({ type: evt, data: data });
				} catch (e) {
					log.warn('evt:', evt, 'data:', data, '回调处理异常 err:', e);
				}
			}
		}
	}

	public Close() {
		if (this.ok) {
			this.ok = false;
			this.listener = {};
		}
	}
}
